import React from "react";
import { Modal, formatDate } from "./UI";
import { REQUEST_SERVICES } from "../lib/requests";
import { markInvoicePaid } from "../lib/invoices";
import { toast } from "./UI";

// Your Whish number — change this!
const WHISH_NUMBER = "+96176760820";

const PACKAGE_ROWS = ({ packages }) => (
  <>
    {packages.map((p, i) => (
      <tr key={p.id}>
        <td style={{ padding: "8px 0", borderBottom: "0.5px solid var(--border)", fontSize: 13, color: "var(--text-muted)", fontFamily: "monospace" }}>{p.id}</td>
        <td style={{ padding: "8px 0", borderBottom: "0.5px solid var(--border)", fontSize: 13 }}>{p.description || "Package"}</td>
        <td style={{ padding: "8px 0", borderBottom: "0.5px solid var(--border)", fontSize: 13, textAlign: "right" }}>{p.weight} lbs</td>
        <td style={{ padding: "8px 0", borderBottom: "0.5px solid var(--border)", fontSize: 13, textAlign: "right" }}>{(parseFloat(p.weight) * 0.453592).toFixed(2)} kg</td>
      </tr>
    ))}
  </>
);

export default function InvoiceModal({ invoice, packages, requests, isAdmin, onClose, onPaid }) {
  const [paying, setPaying] = React.useState(false);
  const [showWhish, setShowWhish] = React.useState(false);

  const svcLabels = (services) => services?.map(id => {
    const s = REQUEST_SERVICES.find(x => x.id === id);
    return s ? `${s.icon} ${s.label} ($${s.price})` : id;
  }) || [];

  const handleMarkPaid = async (method) => {
    setPaying(true);
    try {
      await markInvoicePaid(invoice.id, method);
      toast("Invoice marked as paid!");
      if (onPaid) onPaid();
      onClose();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setPaying(false);
    }
  };

  return (
    <Modal title={`Invoice ${invoice.invoiceNumber}`} onClose={onClose} maxWidth={580}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, paddingBottom: 16, borderBottom: "0.5px solid var(--border)" }}>
        <div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Invoice number</div>
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{invoice.invoiceNumber}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Issued {formatDate(invoice.createdAt)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{
            fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 20,
            background: invoice.status === "Paid" ? "var(--green-light)" : "#FAEEDA",
            color:      invoice.status === "Paid" ? "var(--green-dark)"  : "#854F0B",
          }}>
            {invoice.status === "Paid" ? "✓ Paid" : "⏳ Unpaid"}
          </span>
          {invoice.paidAt && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Paid {formatDate(invoice.paidAt)}</div>}
        </div>
      </div>

      {/* Customer */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Bill to</div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{invoice.customerName}</div>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{invoice.customerEmail}</div>
      </div>

      {/* Shipment */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Shipment</div>
        <div style={{ fontSize: 13, fontFamily: "monospace", color: "var(--text)" }}>{invoice.shipmentTracking}</div>
      </div>

      {/* Packages table */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>Packages</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "left", paddingBottom: 6, borderBottom: "0.5px solid var(--border)" }}>ID</th>
              <th style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "left", paddingBottom: 6, borderBottom: "0.5px solid var(--border)" }}>Description</th>
              <th style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "right", paddingBottom: 6, borderBottom: "0.5px solid var(--border)" }}>Weight (lbs)</th>
              <th style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "right", paddingBottom: 6, borderBottom: "0.5px solid var(--border)" }}>Weight (kg)</th>
            </tr>
          </thead>
          <tbody>
            <PACKAGE_ROWS packages={packages} />
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{ paddingTop: 8, fontSize: 13, fontWeight: 600 }}>Total</td>
              <td style={{ paddingTop: 8, fontSize: 13, fontWeight: 600, textAlign: "right" }}>{invoice.totalWeightLbs} lbs</td>
              <td style={{ paddingTop: 8, fontSize: 13, fontWeight: 600, textAlign: "right" }}>{invoice.totalWeightKg} kg</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Charges */}
      <div style={{ background: "var(--bg-soft)", borderRadius: "var(--radius)", padding: "14px", marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>Charges</div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
          <span>Shipping ({invoice.totalWeightKg} kg × ${invoice.ratePerKg}/kg)</span>
          <span>${invoice.shippingCost}</span>
        </div>
        {parseFloat(invoice.servicesCost) > 0 && (
          <>
            {requests.map(r => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6, color: "var(--text-muted)" }}>
                <span>{svcLabels(r.services).join(", ")}</span>
                <span>${r.totalCost}</span>
              </div>
            ))}
          </>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700, borderTop: "0.5px solid var(--border)", paddingTop: 10, marginTop: 6 }}>
          <span>Total due</span>
          <span style={{ color: "var(--green-dark)" }}>${invoice.totalDue}</span>
        </div>
      </div>

      {/* Admin profit section */}
      {isAdmin && (
        <div style={{ background: "#FAEEDA", borderRadius: "var(--radius)", padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#854F0B", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>Admin — profit breakdown</div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4, color: "#854F0B" }}>
            <span>Revenue from customer</span><span>${invoice.shippingCost}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4, color: "#854F0B" }}>
            <span>Your cost ($12/kg × {invoice.totalWeightKg} kg)</span><span>-${invoice.adminCost}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700, borderTop: "0.5px solid rgba(133,79,11,.2)", paddingTop: 8, marginTop: 4, color: parseFloat(invoice.profit) >= 0 ? "#0F6E56" : "#A32D2D" }}>
            <span>Net profit</span>
            <span>{parseFloat(invoice.profit) >= 0 ? "+" : ""}${invoice.profit}</span>
          </div>
        </div>
      )}

      {/* Payment */}
      {invoice.status === "Unpaid" && !isAdmin && (
        <div>
          {!showWhish ? (
            <button className="btn btn-primary btn-full" onClick={() => setShowWhish(true)} style={{ marginBottom: 8 }}>
              💳 Pay via Whish
            </button>
          ) : (
            <div style={{ background: "#E1F5EE", border: "1.5px solid var(--green-mid)", borderRadius: "var(--radius)", padding: "16px", marginBottom: 8, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "var(--green-dark)", fontWeight: 600, marginBottom: 8 }}>Send payment via Whish</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--green-dark)", marginBottom: 4 }}>{WHISH_NUMBER}</div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Amount: ${invoice.totalDue}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                Use reference: <strong>{invoice.invoiceNumber}</strong>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>After sending, contact us to confirm your payment.</div>
            </div>
          )}
        </div>
      )}

      {/* Admin mark paid */}
      {isAdmin && invoice.status === "Unpaid" && (
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => handleMarkPaid("Whish")} disabled={paying}>
            {paying ? "Saving..." : "✓ Mark paid (Whish)"}
          </button>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => handleMarkPaid("Cash")} disabled={paying}>
            ✓ Mark paid (Cash)
          </button>
        </div>
      )}

      {invoice.status === "Paid" && invoice.paymentMethod && (
        <div style={{ textAlign: "center", fontSize: 13, color: "var(--green-dark)", fontWeight: 600 }}>
          ✓ Paid via {invoice.paymentMethod} on {formatDate(invoice.paidAt)}
        </div>
      )}
    </Modal>
  );
}
